import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense, type ComponentType } from 'react'
import { PrivateRoute } from '@/components/PrivateRoute'
import InternalErrorPage from '@/pages/errors/InternalErrorPage'
import NotFoundPage from '@/pages/errors/NotFoundPage'

const PageSpinner = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin h-8 w-8 border-2 border-hydro-600 border-t-transparent rounded-full" />
  </div>
)

const withSuspense = (Component: ComponentType) => (
  <Suspense fallback={<PageSpinner />}>
    <Component />
  </Suspense>
)

// Lazy imports — code splitting por rota (export default obrigatório)
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'))
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'))
const AcceptInvitePage = lazy(() => import('@/pages/AcceptInvitePage'))

const AppLayout = lazy(() => import('@/components/layout/AppLayout'))
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const DeviceDetailPage = lazy(() => import('@/pages/DeviceDetailPage'))
const SchedulePage = lazy(() => import('@/pages/SchedulePage'))
const HistoryPage = lazy(() => import('@/pages/HistoryPage'))
const ReportsPage = lazy(() => import('@/pages/ReportsPage'))
const AlertsPage = lazy(() => import('@/pages/AlertsPage'))
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const AdminPage = lazy(() => import('@/pages/admin/AdminPage'))

export const router = createBrowserRouter([
  {
    path: '/',
    errorElement: <InternalErrorPage />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: '/login', element: withSuspense(LoginPage) },
      { path: '/forgot-password', element: withSuspense(ForgotPasswordPage) },
      { path: '/reset-password/:token', element: withSuspense(ResetPasswordPage) },
      { path: '/register', element: withSuspense(RegisterPage) },
      { path: '/accept-invite/:token', element: withSuspense(AcceptInvitePage) },
      {
        element: (
          <PrivateRoute>
            {withSuspense(AppLayout)}
          </PrivateRoute>
        ),
        children: [
          { path: '/dashboard', element: withSuspense(DashboardPage) },
          { path: '/reports', element: withSuspense(ReportsPage) },
          { path: '/devices/:id', element: withSuspense(DeviceDetailPage) },
          { path: '/devices/:id/schedule', element: withSuspense(SchedulePage) },
          { path: '/devices/:id/history', element: withSuspense(HistoryPage) },
          { path: '/devices/:id/alerts', element: withSuspense(AlertsPage) },
          { path: '/devices/:id/settings', element: withSuspense(DeviceDetailPage) },
          { path: '/onboarding', element: withSuspense(OnboardingPage) },
          { path: '/settings', element: withSuspense(SettingsPage) },
          {
            path: '/admin',
            element: (
              <PrivateRoute requiredRole="admin">
                {withSuspense(AdminPage)}
              </PrivateRoute>
            ),
          },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
